import { useState, useEffect, useRef } from "react";
import { 
  FaUsers, FaGavel, FaHeartbeat, FaLightbulb, FaLeaf, 
  FaQuoteLeft, FaHandsHelping, FaStar, FaHands, 
  FaShieldAlt, FaUserGraduate, FaArrowRight,
  FaGlobeAfrica, FaTree, FaBullseye, FaRocket,
  FaCheckCircle
} from "react-icons/fa";
import { Link } from "react-router-dom";
import slider1 from "../../assets/images/slider1.jpeg";
import slider2 from "../../assets/images/slider2.jpeg";
import slider3 from "../../assets/images/slider3.jpeg";
import slider4 from "../../assets/images/slider4.jpeg";

function Home() {
  const slides = [slider1, slider2, slider3, slider4];
  const [current, setCurrent] = useState(0);
  const slideRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>

      {/* ============================================================ */}
      {/* HERO SLIDER */}
      {/* ============================================================ */}
      <section className="relative w-full overflow-hidden">
        <div
          ref={slideRef}
          className="flex transition-transform duration-1000 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div key={index} className="w-full flex-shrink-0 relative">
              <img
                src={slide}
                alt={`Slide ${index + 1}`}
                className="w-full h-[500px] md:h-[650px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>

              {index === current && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white px-4 max-w-3xl mx-auto">
                    <span className="inline-block bg-primaryGold text-primaryBlue px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                      Making Impact Since 2019
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
                      ANNUAL <span className="text-primaryGold">IMPACT</span>
                      <br />
                      <span className="text-2xl md:text-3xl font-light">Community Transformation</span>
                    </h1>
                    <p className="text-base md:text-lg mb-6 max-w-2xl mx-auto text-gray-200 leading-relaxed">
                      Empowering women, youth and marginalized communities in Kakamega through gender justice, climate action and sustainable development.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                      <Link to="/donate" className="bg-primaryGold text-primaryBlue px-8 py-3 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-lg">
                        Support Our Work
                      </Link>
                      <Link to="/about" className="bg-white/20 backdrop-blur-sm text-white px-8 py-3 rounded-full font-semibold hover:bg-white/30 transition-all duration-300 border border-white/30">
                        Learn More
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* WHO WE ARE */}
      {/* ============================================================ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-primaryGold text-sm font-semibold uppercase tracking-wider">Who We Are</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primaryBlue mt-2">
              <span className="text-primaryGold">Transforming</span> Communities
            </h2>
            <p className="text-gray-500 mt-2">Advancing Dignity, Building Futures</p>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-justify">
              <span className="text-primaryGold font-bold">Rising to Greatness Organization (RIGO)</span> is a grassroots, women-led community-based organization based in Kakamega, Western Kenya. We work alongside women, girls, survivors of violence, youth, and marginalized communities to build safer, healthier, more just, and economically resilient communities where everyone can rise to their full potential.
            </p>

            <div className="bg-primaryGold/5 border-l-4 border-primaryGold p-4 my-6 rounded-r-lg">
              <p className="text-lg font-semibold text-primaryBlue italic">
                "Our Mantra: <span className="text-primaryGold">Transforming Communities</span>"
              </p>
            </div>

            <p className="text-justify">
              We believe that lasting change begins with empowered people and stronger communities. Our approach is rooted in <span className="font-semibold text-primaryBlue">Ubuntu</span>—the belief that our wellbeing is interconnected—and in the conviction that communities are not simply beneficiaries of development; they are leaders, partners, and agents of change.
            </p>

            <div className="grid md:grid-cols-2 gap-4 my-6">
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-primaryGold">
                <p className="text-sm">
                  <span className="font-semibold text-primaryBlue">RIGO</span> works at the intersection of climate and gender justice, protection, SRHR, economic empowerment, community accountability, and leadership.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-primaryGold">
                <p className="text-sm">
                  We place particular emphasis on women and girls living in rural areas and working in <span className="font-semibold text-primaryBlue">artisanal and small-scale gold mining</span> communities.
                </p>
              </div>
            </div>

            <p className="text-justify">
              Through community-led action, RIGO strengthens access to GBV prevention and response, medical and legal support, psychosocial care, SRHR information and services, grassroots referral pathways, livelihoods, and leadership opportunities.
            </p>

            <div className="bg-primaryBlue text-white p-6 rounded-lg my-6">
              <div className="grid md:grid-cols-2 gap-4 items-center">
                <div>
                  <p className="text-sm text-white/90">
                    Our experience through initiatives such as <span className="text-primaryGold font-semibold">Migodi ya Usawa</span> and <span className="text-primaryGold font-semibold">WAVE</span> has enabled us to support more than
                  </p>
                  <p className="text-4xl font-bold text-primaryGold mt-1">3,000+</p>
                  <p className="text-sm text-white/90">women and girls in artisanal mining communities.</p>
                </div>
                <div className="bg-white/10 p-4 rounded-lg border border-white/20">
                  <p className="text-primaryGold text-xs font-bold uppercase tracking-wider">Policy Impact</p>
                  <p className="text-sm text-white/90 mt-1">
                    Our advocacy contributed to the <span className="text-primaryGold font-semibold">Kakamega County SGBV Control and Management Act 2024</span>.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg my-6">
              <h3 className="text-xl font-bold text-primaryBlue text-center mb-3">
                <FaStar className="inline-block text-primaryGold mr-2" />
                HOW WE CREATE CHANGE
              </h3>
              <p className="text-justify">
                At RIGO, communities are not simply beneficiaries—they are partners, leaders, and agents of change. Our programs are designed with communities, drawing on local knowledge, lived experience, trusted relationships, and a deep commitment to dignity, inclusion, justice, and collective action.
              </p>
              <div className="mt-4 p-4 bg-primaryGold/10 rounded-lg border border-primaryGold/20 text-center">
                <p className="text-primaryBlue font-semibold text-sm">
                  <FaStar className="inline-block text-primaryGold mr-2" />
                  We are transforming communities by investing in people, strengthening systems, amplifying grassroots voices, and creating opportunities for women, girls, and marginalized communities to rise to their full potential.
                  <FaStar className="inline-block text-primaryGold ml-2" />
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* VISION / MISSION / CORE VALUES */}
      {/* ============================================================ */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-primaryGold text-sm font-semibold uppercase tracking-wider">The Foundation</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primaryBlue mt-2">Our <span className="text-primaryGold">Core Identity</span></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border-t-4 border-primaryGold">
              <FaBullseye className="text-primaryGold text-3xl mb-3" />
              <h3 className="text-xl font-bold text-primaryBlue mb-2">VISION</h3>
              <p className="text-gray-600">Empowered and Sustainable Communities.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border-t-4 border-primaryGold">
              <FaRocket className="text-primaryGold text-3xl mb-3" />
              <h3 className="text-xl font-bold text-primaryBlue mb-2">MISSION</h3>
              <p className="text-gray-600">Empowering Youth and Women and Positioning them in Realizing Self-Reliance, Just and Sustainable Communities.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border-t-4 border-primaryGold">
              <FaStar className="text-primaryGold text-3xl mb-3" />
              <h3 className="text-xl font-bold text-primaryBlue mb-2">CORE VALUES</h3>
              <div className="flex flex-wrap gap-2">
                {["Honesty", "Accountability", "Integrity", "Transparency", "Team Work"].map((val) => (
                  <span key={val} className="bg-primaryGold/10 text-primaryBlue px-3 py-1 rounded-full text-xs font-semibold">
                    {val}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* THEMATIC PILLARS */}
      {/* ============================================================ */}
      <section className="py-16 px-4 bg-primaryBlue">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-primaryGold text-sm font-semibold uppercase tracking-wider bg-primaryGold/20 px-4 py-1.5 rounded-full border border-primaryGold/30">Our Focus Areas</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">THEMATIC <span className="text-primaryGold">PILLARS</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { icon: FaUsers, title: "Gender Equality" },
              { icon: FaGavel, title: "Good Governance" },
              { icon: FaHeartbeat, title: "Health & Nutrition" },
              { icon: FaLightbulb, title: "Economic Empowerment" },
              { icon: FaLeaf, title: "Climate Justice" }
            ].map((pillar, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm p-4 rounded-xl text-center hover:bg-white/20 transition-all duration-300 border border-white/10 hover:border-primaryGold"
              >
                <pillar.icon className="text-primaryGold text-3xl mx-auto mb-2" />
                <h4 className="text-white text-sm font-semibold">{pillar.title}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* IMPACT STATS */}
      {/* ============================================================ */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { number: "3,000+", label: "Women & Girls Supported" },
              { number: "5", label: "Thematic Pillars" },
              { number: "2024", label: "SGBV Act Passed" },
              { number: "15+", label: "Communities Reached" }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4">
                <p className="text-3xl font-bold text-primaryGold">{stat.number}</p>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CALL TO ACTION */}
      {/* ============================================================ */}
      <section className="py-12 px-4 bg-primaryGold">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-primaryBlue">Join Us in <span className="text-white">Transforming</span> Communities</h2>
          <p className="text-primaryBlue/80 mt-2 mb-6">Together, we can create lasting change and build a future where everyone can rise to their full potential.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/donate" className="bg-primaryBlue text-white px-8 py-3 rounded-full font-semibold hover:bg-primaryBlue/90 transition-all duration-300 shadow-lg">
              Donate Now
            </Link>
            <Link to="/contact" className="bg-white text-primaryBlue px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg">
              Get Involved
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Home;